import { ChangeDetectionStrategy, Component, Input, OnDestroy, signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { ReplaySubject, filter, pairwise, startWith, takeUntil } from 'rxjs';

import { formatUserName } from 'app/core/utilities/user-util';
import { Loggable } from 'app/utils/logging/loggable';
import { notNil } from 'app/utils/stream-util';
import Social from 'entity/Social';
import User from 'entity/User';

import { ConnectedRoomsService } from '../../services/connected-rooms.service';
import { DevicePermissionService } from '../../services/device-permission/device-permission.service';
import { ConnectedRoomCounts, socialAppSelectors } from '../../state/app';
import { SimpleMediaDeviceInfo, mediaSelectors } from '../../state/media';

@Component({
  selector: 'app-welcome-devices-form',
  templateUrl: './welcome-devices-form.component.html',
  styleUrls: ['./welcome-devices-form.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WelcomeDevicesFormComponent extends Loggable implements OnDestroy {
  private destroyed$ = new ReplaySubject<boolean>(1);
  @Input() public user?: User;
  @Input() public canUserJoin = false;

  public social?: Social;

  public connectedRoomSet = signal<Social[]>([]);
  public cameras = signal<SimpleMediaDeviceInfo[]>([]);
  public mics = signal<SimpleMediaDeviceInfo[]>([]);
  public speakers = signal<SimpleMediaDeviceInfo[]>([]);

  public defaultSelected = 'default';
  public selectedRoom = signal<string | undefined>(undefined);
  private connectedRoomCounts = signal<ConnectedRoomCounts | null>(null);

  public constructor(
    private store: Store,
    private devicePermissionService: DevicePermissionService,
    public connectedRoomsService: ConnectedRoomsService,
  ) {
    super();

    // update signals
    this.store
      .select(mediaSelectors.getCameras)
      .pipe(
        filter((c) => c.length > 0),
        takeUntil(this.destroyed$),
      )
      .subscribe((cameras) => this.cameras.set(cameras));

    this.store
      .select(mediaSelectors.getMics)
      .pipe(
        filter((m) => m.length > 0),
        takeUntil(this.destroyed$),
      )
      .subscribe((mics) => this.mics.set(mics));

    this.store
      .select(mediaSelectors.getSpeakers)
      .pipe(
        filter((s) => s.length > 0),
        takeUntil(this.destroyed$),
      )
      .subscribe((speakers) => this.speakers.set(speakers ?? []));

    this.store
      .select(socialAppSelectors.getCurrentConnectedRoomSet)
      .pipe(filter(notNil), takeUntil(this.destroyed$))
      .subscribe((connectedRoomSet) => this.connectedRoomSet.set(connectedRoomSet.socials ?? []));

    this.store
      .select(socialAppSelectors.getCurrentSocial)
      .pipe(filter(notNil), startWith({} as Social), pairwise(), takeUntil(this.destroyed$))
      .subscribe(([_prevSocial, currentSocial]) => this.selectedRoom.set(currentSocial.socialId));

    this.store
      .select(socialAppSelectors.getCurrentConnectedRoomCounts)
      .pipe(filter(notNil), takeUntil(this.destroyed$))
      .subscribe((updatedCounts: ConnectedRoomCounts) => this.connectedRoomCounts.set(updatedCounts));
  }

  /**
   * Getters and setters
   */
  public get selectedCamera(): SimpleMediaDeviceInfo | null {
    return this.devicePermissionService.selectedCamera;
  }

  public get selectedMic(): SimpleMediaDeviceInfo | null {
    return this.devicePermissionService.selectedMic;
  }

  public get selectedSpeaker(): SimpleMediaDeviceInfo | null {
    return this.devicePermissionService.selectedSpeaker;
  }

  public get disableVideoInput(): boolean {
    return !this.videoPermissionGranted;
  }

  public get disableMicInput(): boolean {
    return !this.audioPermissionGranted;
  }

  public get hideRoomSelectionInput(): boolean {
    return this.connectedRoomsService.dropdownDisabled || !this.connectedRoomsService.isConnected;
  }

  public get audioPermissionGranted(): boolean {
    return this.devicePermissionService.permission.audioGranted;
  }

  public get videoPermissionGranted(): boolean {
    return this.devicePermissionService.permission.videoGranted;
  }

  public get audioPermissionDenied(): boolean {
    return this.devicePermissionService.isAudioPermissionDenied;
  }

  public get videoPermissionDenied(): boolean {
    return this.devicePermissionService.isVideoPermissionDenied;
  }

  public get formTitle(): string {
    if (this.videoPermissionDenied && this.audioPermissionDenied) {
      return `Please configure your Camera and Microphone Permissions`;
    } else if (this.videoPermissionDenied) {
      return `Please configure your Camera Permissions`;
    } else if (this.audioPermissionDenied) {
      return `Please configure your Microphone Permissions`;
    }
    return `${this.formatUserName()}, please set up your devices:`;
  }

  public async setCamera(camera: SimpleMediaDeviceInfo): Promise<void> {
    await this.devicePermissionService.setCamera(camera);
  }

  public async setMic(mic: SimpleMediaDeviceInfo): Promise<void> {
    await this.devicePermissionService.setMic(mic);
  }

  public setSpeaker(speaker: SimpleMediaDeviceInfo) {
    this.devicePermissionService.setSpeaker(speaker);
  }

  public isConnectedRoomFull(room: Social): boolean {
    const roomCount = this.getRoomCount(room) ?? 0;
    const maxParticipants = room.maxParticipants ?? 0;
    return roomCount > maxParticipants - 1;
  }

  public getRoomCount(room: Social): number | undefined {
    if (!this.connectedRoomCounts) {
      return;
    }
    const { socialId } = room ?? {};
    if (!socialId) {
      return 0;
    }
    return this.connectedRoomCounts()?.[socialId] ?? 0;
  }

  public getRoomPopulationText(room: Social): string {
    const actualPop = (this.getRoomCount(room) || 0).toString();
    const totalPop = (room.maxParticipants || 0).toString();
    return `${actualPop}/${totalPop}`;
  }

  public onRoomChange({ socialId }: Social): void {
    this.connectedRoomsService.onRoomChange({ socialId });
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private formatUserName(): string {
    return formatUserName(this.user ?? {});
  }
}
